import _ from 'lodash'
import { useEffect } from 'react'
import { Link, Redirect, useLocation } from 'react-router-dom'

import MESSAGES from 'core/data-model/constants/messages'
import { isCheckBasicCampaignDiscountActive } from 'core/service/basic-plan/basic-plan'
import { ProductCategorySelection } from 'quick-quote/components'
import { isFNAPath } from 'quick-quote/components/navigations/utils'
import { ROUTE_PATH } from 'quick-quote/constants/path'
import { getToggles } from 'quick-quote/feature-toggles'

import { BasicPlanComponent } from './basic-plan'
import { PermissionDenyErrorBox } from './permission-deny-error-box'
import { getServiceWrapper } from 'core/service-wrapper'

const isSelected = (displayProduct, selectedProductCode, userAgentType, selectedDisplayPackageCode) => {
  const campaignDiscount = _.get(displayProduct, 'campaignDiscount')
  const hasBasicCampaignDiscount =
    campaignDiscount === undefined ||
      (typeof campaignDiscount === 'object' && campaignDiscount !== null && Object.keys(campaignDiscount).length === 0)
      ? false
      : true

  // For partner-api products, the cards on this page are deduplicated by `packageCode`
  // (see productList in product-selection/containers.js). The `selectedDisplayProduct.code`
  // in state may point to a sub-product inside the package (e.g. defaultSelectProduct from
  // getProductPackageBySelectedProduct), which won't match any card's `code`.
  // Fall back to matching by `packageCode` so the previously-selected package stays highlighted.
  const matchesByPackage =
    !!displayProduct.packageCode &&
    !!selectedDisplayPackageCode &&
    displayProduct.packageCode === selectedDisplayPackageCode

  if (hasBasicCampaignDiscount) {
    const switchToBasicPlanCode = _.get(campaignDiscount, 'switchToBasicPlanCode')
    const isBasicCampaignDiscountActive = isCheckBasicCampaignDiscountActive({
      basicPlan: displayProduct,
      occupationFactor: undefined,
      userAgentType,
    })

    return switchToBasicPlanCode && isBasicCampaignDiscountActive
      ? switchToBasicPlanCode === selectedProductCode || matchesByPackage
      : displayProduct.code === selectedProductCode || matchesByPackage
  } else {
    return displayProduct.code === selectedProductCode || matchesByPackage
  }
}

const isDisabledProduct = (availablePlanCode, displayProduct) =>
  !!(availablePlanCode && availablePlanCode.length && !availablePlanCode.find((d) => d === displayProduct.code))

export const BasicPlanSelection = ({
  displayProducts,
  productCategories,
  selectedProductCode,
  selectedDisplayPackageCode,
  selectProduct,
  displayCategory,
  selectProductCategory,
  selectedProductCategoryId,
  validateInvestmentProduct,
  validateOrdinaryProduct,
  validateNormalProduct,
  allowProceedInvestment,
  allowProceedOrdinary,
  hideHistory,
  availableCategories,
  availablePlanCode,
  isUserLogin,
  isUserOnline,
  userAgentType,
  isOnFnaPhase,
  ...props
}) => {
  const { needLogin, setNotification, selectedNeedType } = props
  const location = useLocation()

  const isFNA = isFNAPath(location.pathname)

  if (isFNA && !selectedNeedType) {
    return <Redirect to="/" push />
  }

  useEffect(() => {
    if (props.defaultProductCategoryId !== props.selectedProductCategoryId) {
      const serviceWrapper = getServiceWrapper()
      if (serviceWrapper.usePartnerApi) {
        if (selectedProductCategoryId !== props.defaultProductCategoryId) {
          selectProductCategory(props.defaultProductCategoryId)
        }
      }
    }
  }, [selectedProductCategoryId, props.defaultProductCategoryId])

  useEffect(() => {
    if (getToggles().ENABLE_FORCE_LOGIN_FIRST) {
      if (!isUserOnline) {
        setNotification({ type: 'Warning', subType: 'NoInternet' })
      } else if (!isUserLogin) {
        needLogin()
      }
    }
  }, [])

  if (getToggles().ENABLE_FORCE_LOGIN_FIRST) {
    if (!isUserOnline || !isUserLogin) {
      return <Redirect to={ROUTE_PATH.PORTAL} push />
    }
  }

  return (
    <div id="product-selection">
      <div className="content">
        {!hideHistory && (
          <Link to="/e-submission/list" className="list-title">
            <span className="text">{MESSAGES.GO_TO_POLICY_LIST}</span>
          </Link>
        )}
        <PermissionDenyErrorBox permissionResult={allowProceedInvestment} />
        <PermissionDenyErrorBox permissionResult={allowProceedOrdinary} />
        {displayCategory ? (
          <ProductCategorySelection
            categoryList={productCategories}
            availableCategories={availableCategories}
            active={selectedProductCategoryId}
            onChange={(categoryId) => selectProductCategory(categoryId)}
          />
        ) : null}
        <div className="az-product-list product-list">
          {displayProducts.map((displayProduct) => (
            <BasicPlanComponent
              key={displayProduct.code}
              location={location}
              isOnFnaPhase={isOnFnaPhase}
              isSelected={isSelected(displayProduct, selectedProductCode, userAgentType, selectedDisplayPackageCode)}
              displayProduct={displayProduct}
              selectProduct={selectProduct}
              validateInvestmentProduct={validateInvestmentProduct}
              validateOrdinaryProduct={validateOrdinaryProduct}
              validateNormalProduct={validateNormalProduct}
              disabled={isDisabledProduct(availablePlanCode, displayProduct)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}
